import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  adminService,
  AdminEmailLog,
  AdminSubscriptionItem,
  AdminUsageItem,
  AdminUserListItem,
  AdminWebhookEvent,
  ACCOUNT_TYPE_LABELS,
  ACCOUNT_TYPE_OPTIONS,
  STAFF_ROLE_LABELS,
  STAFF_ROLE_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  TIER_LABELS,
  TIER_OPTIONS,
  TIER_REPORT_ALLOWANCES,
  USER_ROLE_OPTIONS,
  USER_STATUS_OPTIONS,
} from '../services/adminService';
import toast from 'react-hot-toast';
import WtiLoader from '../components/WtiLoader';
import '../App.css';

type AdminTab = 'users' | 'subscriptions' | 'usage' | 'webhooks' | 'emails';

function toDateInputValue(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

const emptyCustomerForm = {
  email: '',
  firstName: '',
  lastName: '',
  tierKey: 'explorer' as (typeof TIER_OPTIONS)[number],
  accountType: 'COMPLIMENTARY' as (typeof ACCOUNT_TYPE_OPTIONS)[number],
  accessStartDate: toDateInputValue(),
  accessEndDate: '',
  reportsUnlimited: false,
  reportsAllowance: String(TIER_REPORT_ALLOWANCES.explorer ?? ''),
  sendWelcomeInvitation: true,
  internalNotes: '',
};

const Admin: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<AdminTab>('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionItem[]>([]);
  const [usage, setUsage] = useState<AdminUsageItem[]>([]);
  const [webhooks, setWebhooks] = useState<AdminWebhookEvent[]>([]);
  const [emails, setEmails] = useState<AdminEmailLog[]>([]);
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 'MANAGER' as (typeof STAFF_ROLE_OPTIONS)[number],
  });
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);

  const isAdminPlus = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const reload = async () => {
    setLoading(true);
    try {
      if (tab === 'users') {
        const data = await adminService.listUsers();
        setUsers(data.items);
      } else if (tab === 'subscriptions') {
        const data = await adminService.listSubscriptions();
        setSubscriptions(data.items);
      } else if (tab === 'usage') {
        const data = await adminService.listUsage();
        setUsage(data.items);
      } else if (tab === 'webhooks' && isAdminPlus) {
        const data = await adminService.listWebhookEvents();
        setWebhooks(data.items);
      } else if (tab === 'emails' && isAdminPlus) {
        const data = await adminService.listEmailLogs();
        setEmails(data.items);
      }
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [tab, isAdminPlus]);

  const handleTierChange = (tierKey: (typeof TIER_OPTIONS)[number]) => {
    const planAllowance = TIER_REPORT_ALLOWANCES[tierKey];
    setCustomerForm((prev) => ({
      ...prev,
      tierKey,
      reportsUnlimited: planAllowance === null,
      reportsAllowance: planAllowance === null ? '' : String(planAllowance),
    }));
  };

  const handleUserStatusChange = async (userId: string, status: string) => {
    try {
      await adminService.updateUserStatus(userId, status);
      toast.success('User status updated');
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleUserRoleChange = async (userId: string, role: string) => {
    try {
      await adminService.updateUserRole(userId, role);
      toast.success('User role updated');
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (item: AdminUserListItem) => {
    if (item._id === user?.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    const confirmed = window.confirm(
      `Delete ${item.email} permanently?\n\nThis removes their subscription, usage, evaluations, and magic-login tokens.`
    );
    if (!confirmed) return;

    try {
      await adminService.deleteUser(item._id);
      toast.success('User deleted');
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleCreateStaff = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreatingStaff(true);
    try {
      await adminService.createStaffUser(staffForm);
      toast.success('Staff account created');
      setStaffForm({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        role: 'MANAGER',
      });
      setShowCreateStaff(false);
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create staff account');
    } finally {
      setCreatingStaff(false);
    }
  };

  const handleCreateCustomer = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreatingCustomer(true);
    try {
      let reportsAllowance: number | null | undefined;
      if (customerForm.reportsUnlimited) {
        reportsAllowance = null;
      } else if (customerForm.reportsAllowance.trim() === '') {
        reportsAllowance = undefined;
      } else {
        reportsAllowance = Number(customerForm.reportsAllowance);
        if (!Number.isFinite(reportsAllowance) || reportsAllowance < 0) {
          toast.error('Report allowance must be a non-negative number');
          setCreatingCustomer(false);
          return;
        }
      }

      const result = await adminService.createCustomerUser({
        email: customerForm.email,
        firstName: customerForm.firstName,
        lastName: customerForm.lastName,
        tierKey: customerForm.tierKey,
        accountType: customerForm.accountType,
        accessStartDate: customerForm.accessStartDate
          ? new Date(customerForm.accessStartDate).toISOString()
          : undefined,
        accessEndDate: customerForm.accessEndDate
          ? new Date(customerForm.accessEndDate).toISOString()
          : null,
        reportsAllowance,
        sendWelcomeInvitation: customerForm.sendWelcomeInvitation,
        internalNotes: customerForm.internalNotes.trim() || undefined,
      });

      if (result.invitationSent) {
        toast.success('Customer created — welcome email with magic link sent');
      } else if (customerForm.sendWelcomeInvitation) {
        toast.success(
          `Customer created (email ${result.emailStatus || 'not sent'}${
            result.emailError ? `: ${result.emailError}` : ''
          })`
        );
      } else {
        toast.success('Customer account created');
      }

      setCustomerForm({
        ...emptyCustomerForm,
        accessStartDate: toDateInputValue(),
      });
      setShowCreateCustomer(false);
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create customer account');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleSubscriptionUpdate = async (
    subscriptionId: string,
    field: 'tierKey' | 'status',
    value: string
  ) => {
    try {
      await adminService.updateSubscription(subscriptionId, { [field]: value });
      toast.success('Subscription updated');
      reload();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update subscription');
    }
  };

  return (
    <div className="container admin-page">
      <header className="admin-page__header">
        <div>
          <h1>Admin</h1>
          <p>Operational overview for WTI accounts, usage, and webhooks.</p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </button>
      </header>

      <div className="admin-page__tabs">
        <button
          type="button"
          className={`admin-page__tab ${tab === 'users' ? 'admin-page__tab--active' : ''}`}
          onClick={() => setTab('users')}
        >
          Users
        </button>
        <button
          type="button"
          className={`admin-page__tab ${tab === 'subscriptions' ? 'admin-page__tab--active' : ''}`}
          onClick={() => setTab('subscriptions')}
        >
          Subscriptions
        </button>
        <button
          type="button"
          className={`admin-page__tab ${tab === 'usage' ? 'admin-page__tab--active' : ''}`}
          onClick={() => setTab('usage')}
        >
          Usage
        </button>
        {isAdminPlus && (
          <>
            <button
              type="button"
              className={`admin-page__tab ${tab === 'webhooks' ? 'admin-page__tab--active' : ''}`}
              onClick={() => setTab('webhooks')}
            >
              Webhooks
            </button>
            <button
              type="button"
              className={`admin-page__tab ${tab === 'emails' ? 'admin-page__tab--active' : ''}`}
              onClick={() => setTab('emails')}
            >
              Emails
            </button>
          </>
        )}
      </div>

      {loading ? (
        <WtiLoader message="Loading admin…" />
      ) : (
        <>
          {tab === 'users' && (
            <>
              {isAdminPlus && (
                <div className="admin-create-staff">
                  <div className="admin-create-staff__actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowCreateCustomer((open) => !open);
                        if (!showCreateCustomer) setShowCreateStaff(false);
                      }}
                    >
                      {showCreateCustomer ? 'Cancel' : 'Create user account'}
                    </button>
                    {isSuperAdmin && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => {
                          setShowCreateStaff((open) => !open);
                          if (!showCreateStaff) setShowCreateCustomer(false);
                        }}
                      >
                        {showCreateStaff ? 'Cancel' : 'Create staff account'}
                      </button>
                    )}
                  </div>

                  {showCreateCustomer && (
                    <form className="card admin-create-staff__form" onSubmit={handleCreateCustomer}>
                      <h2>Create user account</h2>
                      <p className="admin-create-staff__hint">
                        Role is set to USER automatically. A magic-link welcome email invites them to
                        set a password (when checked below).
                      </p>
                      <div className="admin-create-staff__grid">
                        <label>
                          First name
                          <input
                            type="text"
                            required
                            value={customerForm.firstName}
                            onChange={(e) =>
                              setCustomerForm({ ...customerForm, firstName: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Last name
                          <input
                            type="text"
                            required
                            value={customerForm.lastName}
                            onChange={(e) =>
                              setCustomerForm({ ...customerForm, lastName: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Email address
                          <input
                            type="email"
                            required
                            value={customerForm.email}
                            onChange={(e) =>
                              setCustomerForm({ ...customerForm, email: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Subscription level
                          <select
                            value={customerForm.tierKey}
                            onChange={(e) =>
                              handleTierChange(e.target.value as (typeof TIER_OPTIONS)[number])
                            }
                          >
                            {TIER_OPTIONS.map((tier) => (
                              <option key={tier} value={tier}>
                                {TIER_LABELS[tier]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Account type
                          <select
                            value={customerForm.accountType}
                            onChange={(e) =>
                              setCustomerForm({
                                ...customerForm,
                                accountType: e.target.value as (typeof ACCOUNT_TYPE_OPTIONS)[number],
                              })
                            }
                          >
                            {ACCOUNT_TYPE_OPTIONS.map((type) => (
                              <option key={type} value={type}>
                                {ACCOUNT_TYPE_LABELS[type]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Access start date
                          <input
                            type="date"
                            required
                            value={customerForm.accessStartDate}
                            onChange={(e) =>
                              setCustomerForm({ ...customerForm, accessStartDate: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Access end date
                          <input
                            type="date"
                            value={customerForm.accessEndDate}
                            onChange={(e) =>
                              setCustomerForm({ ...customerForm, accessEndDate: e.target.value })
                            }
                          />
                          <span className="admin-create-staff__field-hint">
                            Optional — leave blank to use plan duration from start date
                          </span>
                        </label>
                        <label>
                          Report allowance
                          <input
                            type="number"
                            min={0}
                            disabled={customerForm.reportsUnlimited}
                            placeholder={customerForm.reportsUnlimited ? 'Unlimited' : 'From plan'}
                            value={customerForm.reportsUnlimited ? '' : customerForm.reportsAllowance}
                            onChange={(e) =>
                              setCustomerForm({
                                ...customerForm,
                                reportsAllowance: e.target.value,
                              })
                            }
                          />
                          <label className="admin-create-staff__checkbox">
                            <input
                              type="checkbox"
                              checked={customerForm.reportsUnlimited}
                              onChange={(e) =>
                                setCustomerForm({
                                  ...customerForm,
                                  reportsUnlimited: e.target.checked,
                                })
                              }
                            />
                            Unlimited reports
                          </label>
                        </label>
                      </div>
                      <label className="admin-create-staff__notes">
                        Internal notes
                        <textarea
                          rows={3}
                          maxLength={2000}
                          value={customerForm.internalNotes}
                          onChange={(e) =>
                            setCustomerForm({ ...customerForm, internalNotes: e.target.value })
                          }
                          placeholder="Why this account was created, sales notes, etc."
                        />
                      </label>
                      <label className="admin-create-staff__checkbox admin-create-staff__checkbox--block">
                        <input
                          type="checkbox"
                          checked={customerForm.sendWelcomeInvitation}
                          onChange={(e) =>
                            setCustomerForm({
                              ...customerForm,
                              sendWelcomeInvitation: e.target.checked,
                            })
                          }
                        />
                        Send welcome invitation (magic link email to set password)
                      </label>
                      <button type="submit" className="btn btn-primary" disabled={creatingCustomer}>
                        {creatingCustomer ? 'Creating...' : 'Create user account'}
                      </button>
                    </form>
                  )}

                  {isSuperAdmin && showCreateStaff && (
                    <form className="card admin-create-staff__form" onSubmit={handleCreateStaff}>
                      <h2>Create staff account</h2>
                      <p className="admin-create-staff__hint">
                        Create admin, staff (manager), or super admin access. Payment customers are
                        created automatically via GHL, or use Create user account above.
                      </p>
                      <div className="admin-create-staff__grid">
                        <label>
                          Email
                          <input
                            type="email"
                            required
                            value={staffForm.email}
                            onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                          />
                        </label>
                        <label>
                          First name
                          <input
                            type="text"
                            required
                            value={staffForm.firstName}
                            onChange={(e) =>
                              setStaffForm({ ...staffForm, firstName: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Last name
                          <input
                            type="text"
                            required
                            value={staffForm.lastName}
                            onChange={(e) =>
                              setStaffForm({ ...staffForm, lastName: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Role
                          <select
                            value={staffForm.role}
                            onChange={(e) =>
                              setStaffForm({
                                ...staffForm,
                                role: e.target.value as (typeof STAFF_ROLE_OPTIONS)[number],
                              })
                            }
                          >
                            {STAFF_ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {STAFF_ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Password
                          <input
                            type="password"
                            required
                            minLength={6}
                            value={staffForm.password}
                            onChange={(e) =>
                              setStaffForm({ ...staffForm, password: e.target.value })
                            }
                          />
                        </label>
                      </div>
                      <button type="submit" className="btn btn-primary" disabled={creatingStaff}>
                        {creatingStaff ? 'Creating...' : 'Create account'}
                      </button>
                    </form>
                  )}
                </div>
              )}

              <div className="card admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Subscription</th>
                    {isSuperAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map((item) => (
                    <tr key={item._id}>
                      <td>{item.email}</td>
                      <td>
                        {item.firstName} {item.lastName}
                      </td>
                      <td>
                        {isSuperAdmin ? (
                          <select
                            className="admin-inline-select"
                            value={item.role}
                            onChange={(e) => handleUserRoleChange(item._id, e.target.value)}
                          >
                            {USER_ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        ) : (
                          item.role
                        )}
                      </td>
                      <td>
                        {isSuperAdmin ? (
                          <select
                            className="admin-inline-select"
                            value={item.status}
                            onChange={(e) => handleUserStatusChange(item._id, e.target.value)}
                          >
                            {USER_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        ) : (
                          item.status
                        )}
                      </td>
                      <td>{item.subscription?.tierKey || '—'}</td>
                      <td>{item.subscription?.status || '—'}</td>
                      {isSuperAdmin && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm admin-delete-user-btn"
                            disabled={item._id === user?.id}
                            onClick={() => handleDeleteUser(item)}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}

          {tab === 'subscriptions' && (
            <div className="card admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Tier</th>
                    <th>Status</th>
                    <th>Period end</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((item) => (
                    <tr key={item._id}>
                      <td>{item.userId?.email || '—'}</td>
                      <td>
                        {isSuperAdmin ? (
                          <select
                            className="admin-inline-select"
                            value={item.tierKey}
                            onChange={(e) =>
                              handleSubscriptionUpdate(item._id, 'tierKey', e.target.value)
                            }
                          >
                            {TIER_OPTIONS.map((tier) => (
                              <option key={tier} value={tier}>
                                {tier}
                              </option>
                            ))}
                          </select>
                        ) : (
                          item.tierKey
                        )}
                      </td>
                      <td>
                        {isSuperAdmin ? (
                          <select
                            className="admin-inline-select"
                            value={item.status}
                            onChange={(e) =>
                              handleSubscriptionUpdate(item._id, 'status', e.target.value)
                            }
                          >
                            {SUBSCRIPTION_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        ) : (
                          item.status
                        )}
                      </td>
                      <td>
                        {item.currentPeriodEnd
                          ? new Date(item.currentPeriodEnd).toLocaleDateString()
                          : '—'}
                      </td>
                      <td>{new Date(item.updatedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'usage' && (
            <div className="card admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Tier</th>
                    <th>Generated</th>
                    <th>Downloaded</th>
                    <th>Saved</th>
                    <th>Period</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.map((item) => (
                    <tr key={item._id}>
                      <td>{item.userId?.email || '—'}</td>
                      <td>{item.tierKey}</td>
                      <td>{item.reportsGenerated}</td>
                      <td>{item.reportsDownloaded}</td>
                      <td>{item.savedReportsUsed}</td>
                      <td>
                        {item.periodStart && item.periodEnd
                          ? `${new Date(item.periodStart).toLocaleDateString()} – ${new Date(item.periodEnd).toLocaleDateString()}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'webhooks' && isAdminPlus && (
            <div className="card admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Event ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((event) => (
                    <tr key={event._id}>
                      <td>{event.eventId}</td>
                      <td>{event.eventType}</td>
                      <td>{event.status}</td>
                      <td>{new Date(event.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'emails' && isAdminPlus && (
            <div className="card admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Template</th>
                    <th>Status</th>
                    <th>Error</th>
                    <th>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((log) => (
                    <tr key={log._id}>
                      <td>{log.email}</td>
                      <td>{log.template || log.templateKey || '—'}</td>
                      <td>{log.status}</td>
                      <td style={{ maxWidth: 320, whiteSpace: 'normal', fontSize: 12 }}>
                        {log.errorMessage || '—'}
                      </td>
                      <td>{new Date(log.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
